import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../../utils/cn';
import styles from './Checkbox.module.css';
import type { FieldErrorLike } from './fieldUtils';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  error?: FieldErrorLike | undefined;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, error, id: idProp, className, disabled, ...rest },
  ref,
) {
  const reactId = useId();
  const id = idProp ?? `checkbox-${reactId}`;
  const errorMsg = error?.message;
  const errorId = errorMsg ? `${id}-error` : undefined;

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className={cn(
          styles.row,
          errorMsg !== undefined && styles.invalid,
          disabled && styles.disabled,
        )}
      >
        <input
          ref={ref}
          id={id}
          type="checkbox"
          disabled={disabled}
          aria-invalid={errorMsg !== undefined || undefined}
          aria-describedby={errorId}
          className={styles.input}
          {...rest}
        />
        <span className={styles.label}>{label}</span>
      </label>
      {errorMsg ? (
        <p id={errorId} className={styles.error} role="alert">
          {errorMsg}
        </p>
      ) : null}
    </div>
  );
});
